import { useEffect, useState } from 'react';

import { useApolloClient, useLazyQuery } from '@apollo/client';
import { notEmpty, scroll } from '@crehana/utils';

// biome-ignore lint/style/useImportType: legacy import, will be migrated
import { ORDER_BY } from '../constants';
import GET_DOWNLOADABLE_RESOURCES from '../graphql/GetDownloadableResources.graphql';
// biome-ignore lint/style/useImportType: legacy import, will be migrated
import {
  GetDownloadableResources,
  GetDownloadableResourcesVariables,
} from '../graphql/types/GetDownloadableResources';

const ITEMS_PER_FETCH = 12;

const useDownloadableResources = ({
  tags,
  orderBy,
  typeResource,
}: {
  tags?: string | null;
  orderBy: ORDER_BY;
  typeResource?: string | null;
}) => {
  const client = useApolloClient();
  const [currentPage, setCurrentPage] = useState<number>(1);
  const isShowingAllDownloadables = typeResource === 'Todos los descargables';
  const showInitialCacheResult =
    currentPage === 1 && !orderBy && !tags && isShowingAllDownloadables;

  const response = client.cache.readQuery<
    GetDownloadableResources,
    GetDownloadableResourcesVariables
  >({
    query: GET_DOWNLOADABLE_RESOURCES,
    variables: {
      first: ITEMS_PER_FETCH,
    },
  });

  const [fetchDownloadables, { data, loading, error }] = useLazyQuery<
    GetDownloadableResources,
    GetDownloadableResourcesVariables
  >(GET_DOWNLOADABLE_RESOURCES, {
    notifyOnNetworkStatusChange: true,
  });

  // biome-ignore lint/correctness/useExhaustiveDependencies: dependencies are intentional
  useEffect(() => {
    if (currentPage !== 1 && !orderBy && !tags && !typeResource) {
      const after =
        currentPage - 1 !== 0
          ? window.btoa(
              `arrayconnection:${(currentPage - 1) * ITEMS_PER_FETCH - 1}`,
            )
          : '';

      fetchDownloadables({
        variables: {
          first: ITEMS_PER_FETCH,
          tags,
          after,
        },
      });
    }
  }, [currentPage, orderBy, typeResource]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: dependencies are intentional
  useEffect(() => {
    if (orderBy || tags || typeResource) {
      const after =
        currentPage - 1 !== 0
          ? window.btoa(
              `arrayconnection:${(currentPage - 1) * ITEMS_PER_FETCH - 1}`,
            )
          : '';

      fetchDownloadables({
        variables: {
          first: ITEMS_PER_FETCH,
          tags,
          orderBy,
          after,
          typeResource: isShowingAllDownloadables ? null : typeResource,
        },
      });
    }
  }, [orderBy, currentPage, tags, typeResource]);

  // On change filters, rest pagination
  // biome-ignore lint/correctness/useExhaustiveDependencies: dependencies are intentional
  useEffect(() => {
    setCurrentPage(1);
  }, [orderBy, tags, typeResource]);

  // On change page, scroll top
  // biome-ignore lint/correctness/useExhaustiveDependencies: dependencies are intentional
  useEffect(() => {
    scroll.scrollTop();
  }, [currentPage]);

  const totalPages =
    Math.ceil(
      (showInitialCacheResult
        ? response?.resources?.totalCount || 0
        : data?.resources?.totalCount || 0) / ITEMS_PER_FETCH,
    ) || 1;

  const downloadables =
    (showInitialCacheResult ? response : data)?.resources?.edges
      .map(el => el?.node)
      .filter(notEmpty) || [];

  return {
    downloadables,
    loading,
    error,
    currentPage,
    setCurrentPage,
    totalPages,
  };
};

export default useDownloadableResources;
